// CLABE PCF del vendedor: se verifica antes de vincular y, si no aparece, se busca por teléfono.
import { useId, useState } from 'react';
import type { RetailOrg } from '@fiado/type-kit';
import { userMessageOf } from '@/api/errors';
import { Button, Input } from '@/kit';
import { cn } from '@/lib/cn';
import { STATUS_PILL_TONE_CLASS } from '@/shared/patterns/Pills';
import { useLookupRetailUserWallet } from '../api/useRetailOrg';
import {
    clabeDigits,
    isClabe,
    isMxPhone,
    lookupOutcome,
    phoneDigits,
    type LookupOutcome,
    type WalletFieldState,
} from './walletLink';

export interface WalletLinkFieldProps {
    retailerId: string;
    onStateChange: (state: WalletFieldState) => void;
    /** Teléfono de 10 dígitos con que arranca la búsqueda por teléfono. */
    suggestedPhone?: string;
    hint?: string;
    disabled?: boolean;
    /** Por qué el formulario no puede seguir con lo capturado. */
    error?: string | null;
}

export function WalletLinkField({
    retailerId,
    onStateChange,
    suggestedPhone = '',
    hint,
    disabled = false,
    error = null,
}: WalletLinkFieldProps) {
    const phoneId = useId();
    const phoneErrorId = useId();
    const lookup = useLookupRetailUserWallet(retailerId);
    const [clabe, setClabe] = useState('');
    // `null`: la búsqueda por teléfono está cerrada.
    const [phone, setPhone] = useState<string | null>(null);
    const [outcome, setOutcome] = useState<LookupOutcome | null>(null);
    const [lookupError, setLookupError] = useState<string | null>(null);
    const busy = disabled || lookup.isPending;

    const clearResult = () => {
        setOutcome(null);
        setLookupError(null);
    };

    const changeClabe = (text: string) => {
        const digits = clabeDigits(text);
        setClabe(digits);
        setPhone(null);
        clearResult();
        onStateChange(digits === '' ? { kind: 'empty' } : { kind: 'unverified' });
    };

    const openPhone = () => {
        setPhone(suggestedPhone);
        clearResult();
        onStateChange({ kind: 'unverified' });
    };

    const changePhone = (text: string) => {
        setPhone(phoneDigits(text));
        clearResult();
        onStateChange({ kind: 'unverified' });
    };

    const verify = () => {
        const body: RetailOrg.LookupRetailUserWalletRequest =
            phone !== null ? { phone } : { clabe };
        clearResult();
        lookup.mutate(body, {
            onSuccess: (result) => {
                const next = lookupOutcome(result, phone !== null ? 'PHONE' : 'CLABE');
                setOutcome(next);
                onStateChange(next.linkable ? { kind: 'verified', body } : { kind: 'unverified' });
            },
            onError: (failure) => {
                setLookupError(userMessageOf(failure));
                onStateChange({ kind: 'unverified' });
            },
        });
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <Input
                        label="CLABE PCF"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="18 dígitos"
                        hint={hint}
                        error={phone === null ? (error ?? undefined) : undefined}
                        value={clabe}
                        disabled={busy}
                        onChange={(event) => {
                            changeClabe(event.target.value);
                        }}
                    />
                </div>
                {phone === null && (
                    <Button
                        type="button"
                        variant="outline"
                        className="mt-[26px]"
                        disabled={busy || !isClabe(clabe)}
                        isLoading={lookup.isPending}
                        onClick={verify}
                    >
                        Verificar
                    </Button>
                )}
            </div>
            {phone !== null && (
                <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1 space-y-1.5">
                        <label htmlFor={phoneId} className="text-sm font-medium text-foreground">
                            Teléfono del titular
                        </label>
                        <div className="flex h-9 items-stretch overflow-hidden rounded-md border border-input bg-background">
                            <span className="flex items-center border-r border-input bg-muted/40 px-2.5 text-[13px]">
                                +52
                            </span>
                            <input
                                id={phoneId}
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="10 dígitos"
                                className="min-w-0 flex-1 bg-transparent px-2.5 text-[13px] outline-none"
                                value={phone}
                                disabled={busy}
                                aria-invalid={error ? true : undefined}
                                aria-describedby={error ? phoneErrorId : undefined}
                                onChange={(event) => {
                                    changePhone(event.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={busy || !isMxPhone(phone)}
                        isLoading={lookup.isPending}
                        onClick={verify}
                    >
                        Buscar
                    </Button>
                </div>
            )}
            {outcome && (
                <p
                    role="status"
                    className={cn(
                        'rounded-md border px-2.5 py-1.5 text-[11.5px]',
                        STATUS_PILL_TONE_CLASS[outcome.tone],
                    )}
                >
                    {outcome.message}
                </p>
            )}
            {lookupError !== null && (
                <p role="alert" className="text-[11px] text-destructive">
                    {lookupError}
                </p>
            )}
            {outcome && !outcome.found && phone === null && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="self-start"
                    disabled={busy}
                    onClick={openPhone}
                >
                    Buscar por teléfono
                </Button>
            )}
            {phone !== null && error && (
                <p id={phoneErrorId} className="text-[11px] text-destructive">
                    {error}
                </p>
            )}
        </div>
    );
}
